import React from 'react';
import { coreColours, neutralColours, accentColours, typeStyles, radii, shadows, layoutRules, figmaNotes, type Swatch } from '../data/styleGuide';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';

/** /style-guide — the CI reference sheet, written for hand-off to Figma. */
export function StyleGuide() {
  return <div className="w-full bg-paper" data-id="element-980" data-mp="3bgr0ihsi6fkn">
      <PageHero eyebrow="Style Guide" title="Ecoffee Cup® SA — design system." intro="Every colour, type style and measurement used in this build, named for rebuilding as Figma styles. Values here are the source of truth." data-id="element-981" data-mp="3bfplk28d98xn" />

      {/* ── COLOUR ───────────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-982" data-mp="3q6y6hy1bq64g">
        <SectionHeading eyebrow="01 — Colour" title="Core palette." subtitle="Four brand colours carry the site. Everything else supports them." data-id="element-983" data-mp="3u8bg8j7jetgn" />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-984" data-mp="22yf98w3ijy8l">
          {coreColours.map(c => <SwatchCard key={c.hex} swatch={c} large data-id="element-985" data-mp="1yuhaimi3opg2" />)}
        </div>

        <h3 className="type-display mt-20 text-3xl text-coffee md:text-4xl" data-id="element-986" data-mp="1ty0nxwqjuj53">
          Editorial neutrals
        </h3>
        <p className="type-body mt-3 max-w-2xl text-sm text-ink/60" data-id="element-987" data-mp="1ozfmjtzk9tf2">
          Derived from Oat Milk. These do the day-to-day work of backgrounds
          and body copy.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-988" data-mp="2hnuxa4bntpkl">
          {neutralColours.map(c => <SwatchCard key={c.hex} swatch={c} data-id="element-989" data-mp="2cu8qgss6gfwp" />)}
        </div>

        <h3 className="type-display mt-20 text-3xl text-coffee md:text-4xl" data-id="element-990" data-mp="27vp7n18vrk8k">
          Secondary &amp; accent
        </h3>
        <p className="type-body mt-3 max-w-2xl text-sm text-ink/60" data-id="element-991" data-mp="35dpbap7ptk6f">
          Flexible, chosen to sit with the product colourway on the page. Use
          one accent per section, never several at once.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-992" data-mp="ypymii4jkwqp">
          {accentColours.map(c => <SwatchCard key={c.hex} swatch={c} data-id="element-993" data-mp="oy2cc8p6lg64" />)}
        </div>

        <div className="mt-16 rounded-4xl border border-oat/70 bg-paper-warm p-8 md:p-10" data-id="element-994" data-mp="oxsn4nbynd1h">
          <p className="type-eyebrow mb-5 text-[10px] text-pennant" data-id="element-995" data-mp="tvkkhwvdjrd2">
            Opacity conventions
          </p>
          <div className="grid gap-8 sm:grid-cols-3" data-id="element-996" data-mp="f1zdpctjfznx">
            {[{
            label: 'Body copy',
            value: 'Ink @ 65–70%'
          }, {
            label: 'Muted labels',
            value: 'Black Coffee @ 45%'
          }, {
            label: 'Hairline borders',
            value: 'Oat Milk @ 70%'
          }].map(o => <div key={o.label} data-id="element-997" data-mp="b0dtf6eocj3p">
                <p className="type-eyebrow text-[9px] text-coffee/45" data-id="element-998" data-mp="20coaqysl4ou6">
                  {o.label}
                </p>
                <p className="type-ui mt-2 text-base text-coffee" data-id="element-999" data-mp="25b9akx3xd6i3">{o.value}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── TYPOGRAPHY ───────────────────────────────────────── */}
      <section className="w-full bg-paper-warm py-20 md:py-24" data-id="element-1000" data-mp="2aipgv5lcj2q1">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-1001" data-mp="2o8syfj9a6895">
          <SectionHeading eyebrow="02 — Typography" title="Two families, four jobs." subtitle="DM Serif Display for anything that speaks. Gotham for everything that labels, explains or asks to be clicked." data-id="element-1002" data-mp="2oaduxef5o19n" />

          <div className="mt-14 grid gap-6 lg:grid-cols-2" data-id="element-1003" data-mp="2t6kt2l1b88h1">
            {[{
            name: 'DM Serif Display',
            role: 'Headlines & display',
            specimen: 'Aa',
            weights: 'Regular 400 · Italic for pull-quotes',
            tracking: '-3% (-25 to -33)',
            serif: true
          }, {
            name: 'Gotham',
            role: 'Eyebrows · body · CTAs · UI',
            specimen: 'Aa',
            weights: 'Light 300 · Medium 500 · Bold 700',
            tracking: '-2% body · +200 eyebrow · +90 CTA',
            serif: false
          }].map(f => <div key={f.name} className="flex items-center gap-8 rounded-4xl border border-oat/70 bg-paper p-8 md:p-10" data-id="element-1004" data-mp="1jaxr9k9tb0yf">
                <span className={`shrink-0 text-7xl text-coffee md:text-8xl ${f.serif ? 'type-display' : 'font-gotham font-medium'}`} data-id="element-1005" data-mp="rj01e9n6dzqs">
                  {f.specimen}
                </span>
                <div data-id="element-1006" data-mp="10h1ywf7paa9b">
                  <p className="type-eyebrow text-[9px] text-pennant" data-id="element-1007" data-mp="159lbb8unizcj">
                    {f.role}
                  </p>
                  <h3 className="type-display mt-2 text-3xl text-coffee" data-id="element-1008" data-mp="7v92cnczzruz">
                    {f.name}
                  </h3>
                  <p className="type-body mt-2 text-sm text-ink/60" data-id="element-1009" data-mp="crzifaosynge">
                    {f.weights}
                  </p>
                  <p className="type-body text-sm text-ink/60" data-id="element-1010" data-mp="57ymsx8la3dq">
                    Tracking {f.tracking}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Specimen table */}
          <div className="mt-14 overflow-hidden rounded-4xl border border-oat/70 bg-paper" data-id="element-1011" data-mp="3nfxtcg9m3m7c">
            {typeStyles.map((t, i) => <div key={t.figma} className={`grid gap-6 p-8 md:grid-cols-[1.4fr_1fr] md:p-10 ${i > 0 ? 'border-t border-oat/70' : ''}`} data-id="element-1012" data-mp="3ihcvuolqvgml" data-mpi={i}>
                <div className="min-w-0" data-id="element-1013" data-mp="38r1d3vxej2vn">
                  <p className={`${t.preview} break-words`} data-id="element-1014" data-mp="33rygmzvhm4za">{t.sample}</p>
                </div>
                <dl className="space-y-2 md:border-l md:border-oat/70 md:pl-10" data-id="element-1015" data-mp="2zptroefzhwiz">
                  <SpecRow label="Figma style" value={t.figma} strong data-id="element-1016" data-mp="2zrwjn40p5zqc" />
                  <SpecRow label="Font" value={t.family} data-id="element-1017" data-mp="2ursa5xy7i59m" />
                  <SpecRow label="Weight" value={t.weight} data-id="element-1018" data-mp="2l34uwkd49xga" />
                  <SpecRow label="Size" value={t.size} data-id="element-1019" data-mp="2l5r2gk00ccyi" />
                  <SpecRow label="Line height" value={t.lineHeight} data-id="element-1020" data-mp="2p5rfq1bdei3z" />
                  <SpecRow label="Tracking" value={t.tracking} data-id="element-1021" data-mp="2b5qe2jna76ng" />
                  <SpecRow label="Case" value={t.transform} data-id="element-1022" data-mp="2b7b6o3q1cfio" />
                </dl>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── COMPONENTS ───────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-1023" data-mp="38spcaytmgmng">
        <SectionHeading eyebrow="03 — Components" title="The pieces in use." subtitle="Built from the styles above. Every interactive element is a pill." data-id="element-1024" data-mp="38us3hm6igltr" />

        <div className="mt-14 grid gap-6 lg:grid-cols-2" data-id="element-1025" data-mp="2z12ugjms3pbq">
          {/* Buttons */}
          <div className="rounded-4xl border border-oat/70 bg-paper-warm p-8 md:p-10" data-id="element-1026" data-mp="3mrfbck004weg">
            <p className="type-eyebrow mb-8 text-[10px] text-pennant" data-id="element-1027" data-mp="3rl1g7pzxbvzp">
              Buttons
            </p>
            <div className="flex flex-wrap items-center gap-4" data-id="element-1028" data-mp="1g2n4hoheam7i">
              <button className="type-cta rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-1029" data-mp="1b66d8lewfrq7">
                Primary
              </button>
              <button className="type-cta rounded-full border border-coffee/25 px-8 py-4 text-[11px] text-coffee transition-colors hover:bg-coffee hover:text-paper" data-id="element-1030" data-mp="1kuoc2x3mbor7">
                Secondary
              </button>
              <button className="type-cta text-[10px] text-pennant underline decoration-oat underline-offset-[6px]" data-id="element-1031" data-mp="s7aovqpq27ka">
                Text link
              </button>
            </div>
            <dl className="mt-10 space-y-2 border-t border-oat pt-6" data-id="element-1032" data-mp="116pcmiei8413">
              <SpecRow label="Padding" value="32px × 16px" data-id="element-1033" data-mp="119jx29xipoui" />
              <SpecRow label="Radius" value="Pill · 999px" data-id="element-1034" data-mp="2hk5p5x4xkd2x" />
              <SpecRow label="Label" value="CTA/Primary · 11px caps" data-id="element-1035" data-mp="2cmmaiw4blyag" />
              <SpecRow label="Hover" value="Black Coffee → Purple Pennant" data-id="element-1036" data-mp="2cogqrx4nd287" />
              <SpecRow label="Transition" value="colors · 150ms" data-id="element-1037" data-mp="2wq1s0o45earr" />
            </dl>
          </div>

          {/* Chips */}
          <div className="rounded-4xl border border-oat/70 bg-paper-warm p-8 md:p-10" data-id="element-1038" data-mp="31l7b7f661bc5">
            <p className="type-eyebrow mb-8 text-[10px] text-pennant" data-id="element-1039" data-mp="35n2ake4jdena">
              Filter chips
            </p>
            <div className="flex flex-wrap gap-2.5" data-id="element-1040" data-mp="3ahq3qqahix87">
              <span className="type-cta rounded-full border border-coffee bg-coffee px-4 py-2.5 text-[10px] text-paper" data-id="element-1041" data-mp="3fe6p5cqauxhl">
                Selected
              </span>
              <span className="type-cta rounded-full border border-oat px-4 py-2.5 text-[10px] text-coffee/70" data-id="element-1042" data-mp="3pdfiq4h96a3f">
                Default
              </span>
              <span className="type-cta rounded-full border border-pennant bg-pennant px-4 py-2.5 text-[10px] text-paper" data-id="element-1043" data-mp="3tdy87rvob42g">
                Story selected
              </span>
              <span className="type-cta rounded-full border border-pennant/30 px-4 py-2.5 text-[10px] text-pennant" data-id="element-1044" data-mp="1z1huk60jsnnk">
                Story default
              </span>
            </div>
            <dl className="mt-10 space-y-2 border-t border-oat pt-6" data-id="element-1045" data-mp="1mq8ulaxv38el">
              <SpecRow label="Padding" value="16px × 10px" data-id="element-1046" data-mp="1hr4jgerdn0lm" />
              <SpecRow label="Radius" value="Pill · 999px" data-id="element-1047" data-mp="1hrnqv1jei6le" />
              <SpecRow label="Label" value="CTA/Small · 10px caps" data-id="element-1048" data-mp="1dqlq2g9u5mhb" />
              <SpecRow label="Story variant" value="Purple Pennant, to keep the SA layer distinct" data-id="element-1049" data-mp="2ag31lbvi22qx" />
            </dl>
          </div>

          {/* Card */}
          <div className="rounded-4xl border border-oat/70 bg-paper-warm p-8 md:p-10" data-id="element-1050" data-mp="21fd9ai5g6uhy">
            <p className="type-eyebrow mb-8 text-[10px] text-pennant" data-id="element-1051" data-mp="21h7xq6fxh4qs">
              Content card
            </p>
            <div className="overflow-hidden rounded-3xl border border-oat/70 bg-paper" data-id="element-1052" data-mp="pzyf1m4migqj">
              <div className="flex aspect-[4/3] items-center justify-center bg-oat/40" data-id="element-1053" data-mp="l14zzp9ouho9">
                <span className="type-eyebrow text-[9px] text-coffee/40" data-id="element-1054" data-mp="l3hqwrxfc2dg">
                  Image · 4:3
                </span>
              </div>
              <div className="p-7" data-id="element-1055" data-mp="4udu2n69fffv">
                <p className="type-eyebrow text-[9px] text-pennant" data-id="element-1056" data-mp="4vfehi961ea9">Eyebrow</p>
                <h4 className="type-display mt-3 text-2xl text-coffee" data-id="element-1057" data-mp="ilsm1ji5aqz2">
                  Card title
                </h4>
                <p className="type-body mt-3 text-sm text-ink/65" data-id="element-1058" data-mp="dszeknval4ja">
                  Body/Small sits under the title at 65% Ink.
                </p>
                <span className="type-cta mt-6 inline-block text-[10px] text-pennant" data-id="element-1059" data-mp="nklswt2xfjq9">
                  Card action
                </span>
              </div>
            </div>
          </div>

          {/* Form field */}
          <div className="rounded-4xl border border-oat/70 bg-paper-warm p-8 md:p-10" data-id="element-1060" data-mp="xjl0e8byh9bo">
            <p className="type-eyebrow mb-8 text-[10px] text-pennant" data-id="element-1061" data-mp="33c9ksa332m27">
              Form field
            </p>
            <label className="block" data-id="element-1062" data-mp="2zay2yqqgb54k">
              <span className="type-eyebrow mb-3 block text-[9px] text-coffee/50" data-id="element-1063" data-mp="2zbzesxfajvnc">
                Email address
              </span>
              <input readOnly placeholder="you@example.co.za" className="type-body w-full rounded-2xl border border-oat bg-paper px-5 py-4 text-sm text-ink outline-none placeholder:text-ink/35 focus:border-coffee" data-id="element-1064" data-mp="2t7t97swmzr47" />
            </label>
            <dl className="mt-10 space-y-2 border-t border-oat pt-6" data-id="element-1065" data-mp="25h79cowsdd5a">
              <SpecRow label="Label" value="Eyebrow/Small · 9px caps" data-id="element-1066" data-mp="25k1rg9sbulvj" />
              <SpecRow label="Input radius" value="16px" data-id="element-1067" data-mp="2ai3d92h50zap" />
              <SpecRow label="Border" value="Oat Milk → Black Coffee on focus" data-id="element-1068" data-mp="1whskftw9pamw" />
              <SpecRow label="Height" value="56px" data-id="element-1069" data-mp="21fu2qclvd5xb" />
            </dl>
          </div>
        </div>
      </section>

      {/* ── LAYOUT, RADII, SHADOWS ───────────────────────────── */}
      <section className="w-full bg-coffee py-20 text-paper md:py-24" data-id="element-1070" data-mp="3qt7dokohls7e">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-1071" data-mp="3vtd828m55st6">
          <SectionHeading tone="light" eyebrow="04 — Layout & effects" title="Measurements." data-id="element-1072" data-mp="3tfzo0oo99s4l" />

          <div className="mt-14 grid gap-14 lg:grid-cols-[1fr_1fr] lg:gap-20" data-id="element-1073" data-mp="3pcbgu73jcyjv">
            <div data-id="element-1074" data-mp="3kfkyfd49dqva">
              <p className="type-eyebrow mb-7 text-[10px] text-paper/50" data-id="element-1075" data-mp="3kf1rssk26owk">
                Grid & spacing
              </p>
              <dl className="space-y-3" data-id="element-1076" data-mp="3fi1mwkyy3s71">
                {layoutRules.map(r => <div key={r.label} className="flex items-baseline justify-between gap-6 border-b border-paper/15 pb-3" data-id="element-1077" data-mp="h05r4jn9lvdu">
                    <dt className="type-body text-sm text-paper/60" data-id="element-1078" data-mp="76oz9l6ouqct">
                      {r.label}
                    </dt>
                    <dd className="type-ui text-sm text-paper" data-id="element-1079" data-mp="27cbmy7ztf96">{r.value}</dd>
                  </div>)}
              </dl>
            </div>

            <div data-id="element-1080" data-mp="uwsylgcug3y0">
              <p className="type-eyebrow mb-7 text-[10px] text-paper/50" data-id="element-1081" data-mp="uw9k69mejxoq">
                Corner radius
              </p>
              <div className="space-y-4" data-id="element-1082" data-mp="12b39i7aq42fn">
                {radii.map(r => <div key={r.name} className="flex items-center gap-5" data-id="element-1083" data-mp="178bwqmaqawnr">
                    <div aria-hidden="true" className="h-14 w-14 shrink-0 border border-paper/25" style={{
                  borderRadius: r.value
                }} data-id="element-1084" data-mp="1c64yn35014i8" />
                    <div data-id="element-1085" data-mp="1l7bj1narqzgr">
                      <p className="type-ui text-sm text-paper" data-id="element-1086" data-mp="1l60fnol8jtq6">
                        {r.name} · {r.value}
                      </p>
                      <p className="type-body text-xs text-paper/55" data-id="element-1087" data-mp="1uxeahj28s5cz">
                        {r.usage}
                      </p>
                    </div>
                  </div>)}
              </div>

              <p className="type-eyebrow mb-5 mt-12 text-[10px] text-paper/50" data-id="element-1088" data-mp="24wviw88voxcn">
                Shadows
              </p>
              <dl className="space-y-3" data-id="element-1089" data-mp="28xp42tuxry1u">
                {shadows.map(s => <div key={s.name} className="border-b border-paper/15 pb-3" data-id="element-1090" data-mp="36l0qsk8x79mp">
                    <dt className="type-ui text-sm text-paper" data-id="element-1091" data-mp="31o0kq9c2m6vl">{s.name}</dt>
                    <dd className="type-body mt-1 text-xs text-paper/55" data-id="element-1092" data-mp="3fedu8g4m1tn1">
                      {s.value}
                    </dd>
                  </div>)}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* ── FIGMA NOTES ──────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-1093" data-mp="2ip4t95rlk6lg">
        <SectionHeading eyebrow="05 — Taking it to Figma" title="How to set the file up." data-id="element-1094" data-mp="2wo4dm182tzbt" />
        <div className="mt-14 grid gap-x-12 gap-y-10 lg:grid-cols-2" data-id="element-1095" data-mp="2rojenqswpqz3">
          {figmaNotes.map(n => <div key={n.title} className="border-t border-oat pt-7" data-id="element-1096" data-mp="bx3wjstktdu4">
              <h3 className="type-display text-2xl text-coffee" data-id="element-1097" data-mp="6yu0zh3xwb7z">{n.title}</h3>
              <p className="type-body mt-3 text-sm text-ink/65" data-id="element-1098" data-mp="70wuib4awmhg">{n.body}</p>
            </div>)}
        </div>
      </section>
    </div>;
}
function SwatchCard({
  swatch,
  large = false
}: {
  swatch: Swatch;
  large?: boolean;
}) {
  return <div className="overflow-hidden rounded-4xl border border-oat/70 bg-paper-warm" data-id="element-1099" data-mp="5iopcqf1srjx">
      <div className={`flex items-end p-6 ${large ? 'h-40' : 'h-28'}`} style={{
      backgroundColor: swatch.hex
    }} data-id="element-1100" data-mp="afy9prnkyd0w">
        <span className={`type-eyebrow text-[9px] ${swatch.on === 'dark' ? 'text-white/70' : 'text-coffee/50'}`} data-id="element-1101" data-mp="y6u9baya2cip">
          {swatch.token}
        </span>
      </div>
      <div className="p-6" data-id="element-1102" data-mp="og9434o3m9qt">
        <h4 className="type-display text-2xl text-coffee" data-id="element-1103" data-mp="tflz4cp4in4h">{swatch.name}</h4>
        <p className="type-eyebrow mt-3 text-[9px] text-pennant" data-id="element-1104" data-mp="tdj0535ztjnm">
          {swatch.figma}
        </p>
        <dl className="mt-4 space-y-1.5" data-id="element-1105" data-mp="1h33l1rlpiqzj">
          <SpecRow label="HEX" value={swatch.hex} data-id="element-1106" data-mp="1lw6gk5a1oaid" />
          <SpecRow label="RGB" value={swatch.rgb} data-id="element-1107" data-mp="1lxzqbtfyfaul" />
        </dl>
        <p className="type-body mt-4 text-xs text-ink/60" data-id="element-1108" data-mp="1vqu28grhcowm">{swatch.usage}</p>
      </div>
    </div>;
}
function SpecRow({
  label,
  value,
  strong = false
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return <div className="flex items-baseline justify-between gap-4" data-id="element-1109" data-mp="1lvsgzw0bs6zb">
      <dt className="type-eyebrow shrink-0 text-[8px] text-coffee/40" data-id="element-1110" data-mp="1hr2moblbf0gi">
        {label}
      </dt>
      <dd className={`type-ui text-right text-xs ${strong ? 'text-pennant' : 'text-coffee'}`} data-id="element-1111" data-mp="1cu2oemupj1zy">
        {value}
      </dd>
    </div>;
}